import { FieldType } from "@/components/form-fields/types";
import { ColumnDef } from "@tanstack/react-table";
import { z, ZodTypeAny } from "zod";

/** Layout node: row (flex row). */
export interface RowLayoutElement {
  type: "row";
  className?: string;
  children: FormLayoutElement[];
}

/** Layout node: column (flex col). */
export interface ColLayoutElement {
  type: "col";
  className?: string;
  children: FormLayoutElement[];
}

/** Tree node for form layout: either a field config or a row/col container. */
export type FormLayoutElement = FieldConfig | RowLayoutElement | ColLayoutElement;

export function isRowLayoutElement(
  e: FormLayoutElement,
): e is RowLayoutElement {
  return (
    e !== null &&
    typeof e === "object" &&
    "type" in e &&
    (e as RowLayoutElement).type === "row"
  );
}

export function isColLayoutElement(
  e: FormLayoutElement,
): e is ColLayoutElement {
  return (
    e !== null &&
    typeof e === "object" &&
    "type" in e &&
    (e as ColLayoutElement).type === "col"
  );
}

export function isLayoutElement(
  e: FormLayoutElement,
): e is RowLayoutElement | ColLayoutElement {
  return isRowLayoutElement(e) || isColLayoutElement(e);
}

/** Flatten layout tree to field configs only (for schema or other uses). */
export function flattenLayoutToFieldConfigs(
  elements: FormLayoutElement[],
): FieldConfig[] {
  const out: FieldConfig[] = [];
  for (const el of elements) {
    if (isLayoutElement(el)) {
      out.push(...flattenLayoutToFieldConfigs(el.children));
    } else {
      out.push(el);
    }
  }
  return out;
}

export interface ConfigurationTypeFile {
  fields: FormLayoutElement[];
  onSubmit?: (data: never) => void;
}

export type FieldConfigOverrides = Record<string, Partial<FieldConfig>>;

export interface FieldValidation {
  required?: boolean;
  minLength?: number;
  min?: number;
  email?: boolean;
}

export interface FieldOption {
  /** The text to display for the option. */
  label: string;
  /** The unique value associated with the option. */
  value: string;
  /** Optional icon component to display alongside the option. */
  icon?: React.ComponentType<{ className?: string }>;
}

export interface FieldConfig {
  name: string;
  type: FieldType;
  label?: string;
  tooltip?: string;
  expandOnOverflow?: boolean;
  className?: string;
  placeholder?: string;
  separator?: boolean;
  validation?: FieldValidation;
  disabled?: boolean;
  links?: {
    name: string;
    label: string;
    type: FieldType;
    fields?: FormLayoutElement[];
    hidden?: boolean;
  }[]; // Use for Links type
  list?: string; // Use for multiSelect type ModifiableList
  options?: () => Promise<FieldOption[]>; // only for select type
  showWhen?: (values: any) => boolean;
  group?: "left" | "right";
  linkType?: string;
  defaultSelectValue?: string;
  maxKey?: number;
  subfields?: FieldConfig[];
  table?: {
    columns: ColumnDef<any, any>[];
    actions: { name: string; label: string }[];
  };
  radioChoices?: FieldOption[]; // only for radio type
  /** Always visible; no eye toggle (required / critical fields). */
  nonMaskable?: boolean;
  /** When false, field masking is disabled for this field. */
  maskable?: boolean;
}

export function applyFieldConfigOverrides(
  elements: FormLayoutElement[],
  overrides: FieldConfigOverrides,
): FormLayoutElement[] {
  return elements.map((element) => {
    if (isLayoutElement(element)) {
      return {
        ...element,
        children: applyFieldConfigOverrides(element.children, overrides),
      };
    }

    const field = element as FieldConfig;
    const override = overrides[field.name];
    if (!override) {
      return field;
    }

    return {
      ...field,
      ...override,
    };
  });
}

export const createSchema = (config: FieldConfig[]): z.ZodObject<any> => {
  const shape: Record<string, ZodTypeAny> = {};

  config.forEach((field) => {
    let schema: ZodTypeAny;

    if (field.type === "dontUse" || field.type === "image") {
      return;
    }

    switch (field.type) {
      case "text": {
        schema = z.string().optional();

        if (true === field.validation?.required)
          schema = z.string().trim().min(1);

        break;
      }

      case "number": {
        let numberSchema: any = z.number();

        if (field.validation) {
          if (field.validation.required) {
            numberSchema = numberSchema.refine(
              (val: any) => val !== undefined && val !== null,
              {
                message: `${field.label} est requis`,
              },
            );
          }
          if (field.validation.min !== undefined) {
            numberSchema = numberSchema.min(field.validation.min, {
              message: `${field.label} doit être au moins ${field.validation.min}`,
            });
          }
        }

        schema = numberSchema;
        break;
      }

      case "email": {
        let emailSchema = z.string().email({ message: "Email invalide" });

        if (field.validation && field.validation.required) {
          emailSchema = emailSchema.min(1, {
            message: `${field.label} est requis`,
          });
        }

        schema = emailSchema;
        break;
      }

      case "select": {
        let selectSchema: any = z.string();

        if (field.validation && field.validation.required) {
          selectSchema = selectSchema.min(1, {
            message: `${field.label} est requis`,
          });
        }

        schema = selectSchema;
        break;
      }

      case "set": {
        let setSchema = z.object({});

        if (field.subfields) {
          const subfieldSchema = createSchema(field.subfields);
          setSchema = z.object(subfieldSchema.shape);
        }

        schema = setSchema;
        break;
      }

      case "password": {
        let passwordSchema = z.string();

        if (field.validation) {
          if (field.validation.required) {
            passwordSchema = passwordSchema.min(1, {
              message: `${field.label} est requis`,
            });
          }
          if (field.validation.minLength) {
            passwordSchema = passwordSchema.min(field.validation.minLength, {
              message: `${field.label} doit contenir au moins ${field.validation.minLength} caractères`,
            });
          }
        }

        schema = passwordSchema;
        break;
      }

      default: {
        schema = z.any();
        break;
      }
    }

    shape[field.name] = schema;
  });

  return z.object(shape);
};
